import type { DocAiDocument } from '@/types/api-response/doc-ai-types';
import type { MeasureRead } from '@/types/measure-document-types';

const BUREAU_PATH = 'DOCUMENT.ATTRIBUTE.CLASSIFICATION.BUREAU';
const DOCTYPE_PATH = 'DOCUMENT.ATTRIBUTE.CLASSIFICATION.DOCTYPE';
const FILE_NAME_PATH = 'DOCUMENT.ATTRIBUTE.IDENTIFIER.FILE_NAME';
const DOCURL_PATH = 'DOCUMENT.ATTRIBUTE.ACCESS.DOCURL';

/**
 * Flatten a `MeasureRead` into the row shape the legacy document table renders.
 *
 * Dimensions are keyed by their **full codeset path** rather than a guessed
 * legacy field name: `findKnownColumn` already resolves a dotted path to its
 * column config (and `humanizeFieldName` to a label), so paths are what the
 * table is built to accept — and unlike a name guess, a path can't silently
 * become the wrong column.
 *
 * The handful of top-level keys (`docid`, `id`, `Bureau`, `DocType`,
 * `filename`, `docurl`) are lifted out because code around the table reads them
 * directly. `id` is the Measure PK — what the document-link endpoint is keyed
 * by — while `docid` remains the NMED docid.
 */
export function measureToDocAiDocument(measure: MeasureRead): DocAiDocument {
   const byPath = new Map<string, string>();

   for (const dimension of measure.dimensions ?? []) {
      const path = dimension.dimension_type?.path;
      if (!path)
         continue;

      const value = dimension.result_text
         ?? (dimension.result_number != null ? String(dimension.result_number) : null)
         ?? dimension.result_date;

      // An empty dimension would otherwise surface as a blank column, which
      // reads as "we know this is empty" rather than "not recorded".
      if (value == null || value === '')
         continue;

      byPath.set(path, value);
   }

   const docid = measure.result_text ?? '';

   const doc: Record<string, unknown> = {
      ...Object.fromEntries(byPath),
      docid,
      id: measure.id,
      docurl: byPath.get(DOCURL_PATH) ?? '',
      filename: byPath.get(FILE_NAME_PATH) ?? docid,
      Bureau: byPath.get(BUREAU_PATH) ?? measure.tenant?.name ?? '',
      DocType: byPath.get(DOCTYPE_PATH) ?? null,
   };

   return doc as DocAiDocument;
}

/**
 * Distinct dimension paths present across a set of rows, in first-seen order.
 *
 * Replaces the legacy API's `search_fields`: the columns worth showing are the
 * ones this result set actually populates.
 */
export function collectDimensionPaths(documents: DocAiDocument[]): string[] {
   const seen = new Set<string>();
   for (const doc of documents) {
      for (const key of Object.keys(doc)) {
         if (key.includes('.'))
            seen.add(key);
      }
   }
   return [...seen];
}
